import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { AdminApi, PushContentInput, PushValidationResult } from '../../../api/client.ts';
import { emptyResult, mergeResults, problemIssues, structuralIssues } from './pushChecks.ts';
import type { PushPlatform } from './pushDraft.ts';

export interface ValidationTarget {
  /** The platforms this content is written for (one in separate mode). */
  platforms: readonly PushPlatform[];
  content: PushContentInput;
}

/** Pause after the last edit before the content is checked again. */
export const VALIDATE_DELAY_MS = 600;

/**
 * Checks the contents being edited (BB-73): the contract's structure in the browser, then `POST …/push/validate` for
 * each content that passes it, `VALIDATE_DELAY_MS` after the last edit (or at once with `checkNow`). The last result
 * stays on screen while the next check runs. Read-only messages are not checked.
 */
export function usePushValidation(environmentId: string, category: 'marketing' | 'transactional', targets: readonly ValidationTarget[], enabled = true) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const json = JSON.stringify(targets);
  const [checked, setChecked] = useState(json);
  useEffect(() => {
    if (json === checked) return undefined;
    const timer = setTimeout(() => setChecked(json), VALIDATE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [json, checked]);

  const query = useQuery({
    queryKey: ['push-validate', environmentId, category, checked],
    enabled,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    placeholderData: (previous) => previous,
    queryFn: async (): Promise<PushValidationResult> => {
      const list = JSON.parse(checked) as ValidationTarget[];
      const results = await Promise.all(
        list.map(async (t) => {
          const structural = structuralIssues(t.content);
          if (structural.length) return { platforms: t.platforms, result: emptyResult(structural) };
          try {
            return { platforms: t.platforms, result: await api.validatePush(environmentId, { content: t.content, category }) };
          } catch (error) {
            const issues = problemIssues(error);
            if (issues) return { platforms: t.platforms, result: emptyResult(issues) };
            throw error;
          }
        }),
      );
      return mergeResults(results);
    },
  });

  return {
    result: query.data,
    error: query.error,
    /** A check is running, or edits wait for the pause before the next one. */
    pending: query.isFetching || json !== checked,
    checkNow: () => (json === checked ? void query.refetch() : setChecked(json)),
  };
}
